/**
 * DocumentUploadSection Component
 *
 * Handles uploading source CD documents for reconciliation.
 * Features:
 * - Drag and drop file upload
 * - File type validation (PDF only)
 * - Upload progress indicator
 * - List of uploaded documents with download/delete
 */

import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Loader2,
  Trash2,
  Upload,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  useDeleteDocument,
  useDownloadDocument,
  useSourceDocuments,
  useUploadDocument,
} from '../../hooks';
import { useLatestVersion } from '../../hooks/useVersions';
import type { CDDocument, ExtractionStatus } from '../../types';

export interface DocumentUploadSectionProps {
  /**
   * Transaction ID
   */
  transactionId: string;

  /**
   * Whether to automatically create a version when uploading
   */
  createVersionOnUpload?: boolean;

  /**
   * Callback when a new version is created from upload
   */
  onVersionCreated?: (versionNumber: number) => void;

  /**
   * Whether uploads are disabled
   */
  disabled?: boolean;
}

const extractionStatusConfig: Record<
  ExtractionStatus,
  {
    label: string;
    icon: React.ReactNode;
    variant: 'default' | 'secondary' | 'outline' | 'destructive';
  }
> = {
  pending: {
    label: 'Pending',
    icon: <Clock className="h-3 w-3" />,
    variant: 'outline',
  },
  processing: {
    label: 'Processing',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
    variant: 'secondary',
  },
  completed: {
    label: 'Extracted',
    icon: <CheckCircle2 className="h-3 w-3" />,
    variant: 'default',
  },
  failed: {
    label: 'Failed',
    icon: <AlertCircle className="h-3 w-3" />,
    variant: 'destructive',
  },
  skipped: {
    label: 'Skipped',
    icon: <Clock className="h-3 w-3" />,
    variant: 'outline',
  },
};

export function DocumentUploadSection({
  transactionId,
  createVersionOnUpload = true,
  onVersionCreated,
  disabled = false,
}: DocumentUploadSectionProps) {
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<CDDocument | null>(null);

  const { data: documents = [], isLoading } = useSourceDocuments(transactionId);
  const { refetch: refetchLatestVersion } = useLatestVersion(transactionId);
  const uploadMutation = useUploadDocument();
  const downloadMutation = useDownloadDocument();
  const deleteMutation = useDeleteDocument();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      if (!file) return;

      setUploadProgress(0);

      try {
        // Simulate progress (actual progress would come from upload)
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev === null || prev >= 90) {
              clearInterval(progressInterval);
              return prev;
            }
            return prev + 10;
          });
        }, 200);

        await uploadMutation.mutateAsync({
          transactionId,
          file,
          documentType: 'source_cd',
          createVersion: createVersionOnUpload,
        });

        clearInterval(progressInterval);
        setUploadProgress(100);

        // Fetch the latest version to get the actual version number
        if (onVersionCreated && createVersionOnUpload) {
          const { data: latestVersion } = await refetchLatestVersion();
          if (latestVersion) {
            onVersionCreated(latestVersion.versionNumber);
          }
        }

        // Reset progress after a short delay
        setTimeout(() => setUploadProgress(null), 1000);
      } catch {
        setUploadProgress(null);
      }
    },
    [transactionId, createVersionOnUpload, uploadMutation, onVersionCreated, refetchLatestVersion],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    disabled: disabled || uploadMutation.isPending,
  });

  const handleDownload = async (document: CDDocument) => {
    try {
      // The mutation handles the download in its onSuccess callback
      await downloadMutation.mutateAsync({
        transactionId,
        documentId: document.id,
      });
    } catch {
      // Error handled by mutation
    }
  };

  const handleDeleteClick = (document: CDDocument) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      await deleteMutation.mutateAsync({
        transactionId,
        documentId: documentToDelete.id,
      });

      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch {
      // Error handled by mutation
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">Source Documents</h3>
        <p className="text-sm text-muted-foreground">
          Upload the original Closing Disclosure PDF for reconciliation.
        </p>
      </div>

      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
          ${disabled || uploadMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        {uploadProgress !== null ? (
          <div className="space-y-2">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">Uploading document...</p>
            <Progress value={uploadProgress} className="w-48 mx-auto" />
          </div>
        ) : (
          <>
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium">
              {isDragActive ? 'Drop the PDF here' : 'Drag & drop a PDF file here'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">or click to select a file</p>
          </>
        )}
      </div>

      {uploadMutation.error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {uploadMutation.error.message}
        </div>
      )}

      {/* Document List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : documents.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Uploaded Documents</h4>
          <div className="space-y-2">
            {documents.map((doc: CDDocument) => (
              <DocumentListItem
                key={doc.id}
                document={doc}
                onDownload={handleDownload}
                onDelete={handleDeleteClick}
                isDownloading={downloadMutation.isPending}
                formatFileSize={formatFileSize}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{documentToDelete?.fileName}&quot;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface DocumentListItemProps {
  document: CDDocument;
  onDownload: (document: CDDocument) => void;
  onDelete: (document: CDDocument) => void;
  isDownloading: boolean;
  formatFileSize: (bytes: number) => string;
}

function DocumentListItem({
  document,
  onDownload,
  onDelete,
  isDownloading,
  formatFileSize,
}: DocumentListItemProps) {
  const statusConfig = extractionStatusConfig[document.extractionStatus];

  return (
    <div className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-3">
        <FileText className="h-8 w-8 text-red-500" />
        <div>
          <p className="text-sm font-medium">{document.fileName}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{formatFileSize(document.fileSize)}</span>
            <span>-</span>
            <span>{formatDistanceToNow(new Date(document.createdAt), { addSuffix: true })}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Badge variant={statusConfig.variant} className="flex items-center gap-1">
          {statusConfig.icon}
          {statusConfig.label}
        </Badge>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDownload(document)}
          disabled={isDownloading}
        >
          <Download className="h-4 w-4" />
        </Button>

        <Button variant="ghost" size="icon" onClick={() => onDelete(document)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
